export class Lightbox extends HTMLElement {
    /*
    - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
    **
    **  This is a Custom HTML Element that displays images within a modal that can be zoomed
    **  and viewed at higher resolutions.
    **
    **  Dependencies:
    **      - qf-lightbox.liquid            - This is the Lightbox element.
    **      - qf-lightbox-trigger.liquid    - This is used to open the modal and define what image sources should be loaded.
    **
    **  Author: Joseph Bowman (jo@quickfiredigital.com)
    **
    - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
    */
    static observedAttributes = ["open"];

    constructor () {
        super();

        this.selectors = {
            classes: {
                modal: {
                    close: 'lightbox-modal-close',
                },
                swiper: {
                    wrapper: 'swiper-wrapper',
                }
            },
            attributes: {
                lightbox: {
                    trigger: 'lightbox-trigger',
                    close: 'lightbox-close'
                }
            }
        };

        this.lightboxSwiper = null;
    }



    /**
     * Initializes the lightbox and sets up event listeners.
     * This method is automatically called when the component is connected to the DOM.
     */
    init() {
        this.addEventListener('click', this.handleEvent.bind(this));
        // this.addEventListener('close', this.handleClose.bind(this));
    }

    /**
     * Invoked when the element is added to the DOM.
     * Automatically calls the `init` method to initialize the lightbox.
     */
    connectedCallback() {
        // console.log('📸 ✅ Lightbox has been ADDED to the DOM!');
        this.init();
    }



    /**
     * Invoked when the element is removed from the DOM.
     * Cleans up the swiper instance to avoid memory leaks.
     */
    disconnectedCallback() {
        // console.log('📸 🚫 Lightbox has been REMOVED from the DOM!');
        if (this.lightboxSwiper) {
            this.lightboxSwiper.destroy();
            this.lightboxSwiper = null;
        }
    }

    attributeChangedCallback(name, oldValue, newValue) {
        console.log(`Attribute ${name} has changed.`);
        if (name === 'open') {
            if (newValue !== null) {
                // Lightbox is open
                this.setAttribute('aria-hidden', 'false');
            } else {
                // Lightbox is closed
                this.setAttribute('aria-hidden', 'true');
            }
        }
      }



    /**
     * Shows the lightbox by setting the 'open' attribute on the element
     * and disabling scrolling on the page.
     */
    showLightbox() {
        this.setAttribute('open', '');

        // Stop Scrolling
        document.documentElement.style.overflow = 'hidden';
        document.documentElement.style.maxHeight = '100vh';
    }



    /**
     * Closes the lightbox by removing the 'open' attribute and re-enabling page scrolling.
     * Also removes the lightbox from the DOM.
     */
    closeLightbox() {
        this.removeAttribute('open');

        // Allow Scrolling
        document.documentElement.style.overflow = '';
        document.documentElement.style.maxHeight = '';

        this.remove();
    }



    /**
     * Helper method to create a swiper slide element for a given image source.
     *
     * @param {string} src - The image source URL.
     * @returns {HTMLElement} - The created swiper slide element.
     */
    createSwiperSlide(src) {
        const slideNode = document.createElement('div');
        slideNode.classList.add('swiper-slide', 'lightbox-slide');

        const zoomNode = document.createElement('div');
        zoomNode.classList.add('swiper-zoom-container');

        let formattedSource = new URL(src, window.location.origin);
        if (formattedSource.searchParams.get('width') < 2000) {
            formattedSource.searchParams.set('width', 2000);
        }

        const slideImg = new Image();
        slideImg.src = formattedSource.href;
        zoomNode.appendChild(slideImg);
        slideNode.appendChild(zoomNode);

        return slideNode;
    }



    /**
     * Initializes the swiper instance to handle the image slideshow with zoom functionality.
     */
    initSwiper() {
        // console.log('📸 Initializing Swiper for Lightbox');
        this.lightboxSwiper = new window.swiper(this, {
            slidesPerView: 1,
            spaceBetween: 500,
            navigation: {
                nextEl: '.lightbox-modal-navigation.swiper-button-next',
                prevEl: '.lightbox-modal-navigation.swiper-button-prev',
            },
            zoom: {
                maxRatio: 3,
            },
            keyboard: {
                enabled: true,
                onlyInViewport: false,
            },
            on: {
                afterInit: () => {
                    // console.log('📸 Initialised Lightbox Swiper');
                    this.removeAttribute('modal-loading');
                }
            }
        });
    }



    /**
     * Handles lightbox events such as closing the lightbox when the close button is clicked.
     *
     * @param {Event} e - The event triggered by the user's interaction with the element.
     */
    handleEvent(e) {
        const closeTrigger = e.target.closest(`[${this.selectors.attributes.lightbox.close}]`);
        if (closeTrigger) {
            // console.log('📸 Closing Lightbox');
            this.closeLightbox();
        }
    }



    /**
     * Opens the lightbox and loads the images based on the provided lightbox trigger element.
     *
     * @param {HTMLElement} lightboxTrigger - The element that triggered the lightbox to open (e.g., an image or button).
     */
    openLightbox(lightboxTrigger) {
        // console.log('📸 Opening Lightbox');
        const lightboxGroup = lightboxTrigger.dataset.lightboxGroup;
        const triggerSource = lightboxTrigger.dataset.lightboxSrc;

        // Fetch lightbox sources based on group or single source
        const lightboxSources = this.getLightboxSources(lightboxGroup, triggerSource);

        // Load swiper with images from the sources
        this.loadSwiperSlides(lightboxSources, triggerSource);

        // Open Lightbox Modal
        this.showLightbox();
    }



    /**
     * Helper method to fetch all lightbox sources, either from a group of images or a single source.
     *
     * @param {string|null} lightboxGroup - The group identifier for the images (optional).
     * @param {string} triggerSource - The image source URL of the clicked lightbox trigger.
     * @returns {string[]} - An array of image source URLs.
     */
    getLightboxSources(lightboxGroup, triggerSource) {
        let lightboxSources = [];

        if (lightboxGroup) {
            // Add all sources if group exists
            // console.log('📸 Fetching group sources');
            const groupTriggers = document.querySelectorAll(`[lightbox-trigger][data-lightbox-group="${lightboxGroup}"]`);
            groupTriggers.forEach(groupTrigger => {
                const src = groupTrigger.dataset.lightboxSrc;
                if (src) {
                    lightboxSources.push(src);
                }
            });
        } else {
            // Add single source
            // console.log('📸 Fetching single source');
            lightboxSources.push(triggerSource);
        }

        return lightboxSources;
    }



    /**
     * Helper method to create and load swiper slides based on the provided image sources.
     *
     * @param {string[]} lightboxSources - An array of image source URLs.
     * @param {string} triggerSource - The image source of the clicked lightbox trigger.
     */
    loadSwiperSlides(lightboxSources, triggerSource) {
        // console.log('📸 Loading Swiper slides');
        const slideElements = [];
        const swiperWrapper = this.querySelector(`.${this.selectors.classes.swiper.wrapper}`);

        // Clear existing slides
        swiperWrapper.innerHTML = '';

        let selectedIndex = 0;

        // Generate image slides from the sources
        lightboxSources.forEach((src, index) => {
            slideElements.push(this.createSwiperSlide(src));
            if (src === triggerSource) selectedIndex = index;
        });

        // Append slides to the swiper wrapper
        swiperWrapper.append(...slideElements);

        // Initialize swiper if not already done
        if (!this.lightboxSwiper) {
            this.initSwiper();
        }

        // Update swiper and set the selected slide
        this.lightboxSwiper.update();
        this.lightboxSwiper.slideTo(selectedIndex, 0, true);
    }

}

if (!customElements.get('lightbox-modal')) {
    customElements.define('lightbox-modal', Lightbox);
    // console.log('📸 Lightbox element defined');

    /**
     * Creates the lightbox modal element and opens it with the provided trigger.
     *
     * @param {HTMLElement} lightboxTrigger - The element that triggered the lightbox.
     */
    async function createLightbox(lightboxTrigger) {
        // console.log('📸 Fetching Lightbox HTML');
        return fetch(window.Shopify.routes.root + "?section_id=qf-lightbox")
        .then((response) => {
            if (!response.ok) {
                throw new Error("Network response was not ok");
            }
            return response.text();
        })
        .then((htmlString) => {
            const html = new DOMParser().parseFromString(htmlString, 'text/html');
            const lightboxElement = html.querySelector('lightbox-modal');

            if (lightboxElement) {
                // Add the lightbox HTML to the body
                document.body.appendChild(lightboxElement);

                // Pass trigger data to the Lightbox instance
                lightboxElement.openLightbox(lightboxTrigger);
            }
        })
        .catch((error) => {
            console.error("Error:", error);
            alert("Failed to load lightbox. Please try again.");
        });
    }

    /**
     * Sets up event listeners for lightbox trigger elements to open the lightbox.
     */
    document.body.addEventListener('click', (e) => {
        const lightboxTrigger = e.target.closest('[lightbox-trigger]');

        if (lightboxTrigger && !lightboxTrigger.disabled) {
            lightboxTrigger.disabled = true;
            createLightbox(lightboxTrigger)
            .then(() => {
                lightboxTrigger.disabled = false;
            });
        }
    });
}